'use client';

import { Box, Container, Stack, Typography } from '@mui/material';

import type { ReactNode } from 'react';

type ErrorPageLayoutProps = {
    code: string;
    title: string;
    description: string;
    actions: ReactNode;
    gradient?: string;
    haloColor?: string;
};

const DEFAULT_GRADIENT =
    'linear-gradient(120deg, #1faba7 0%, #0d6e6b 35%, #1faba7 70%, #2fd6d1 100%)';

export function ErrorPageLayout({
    code,
    title,
    description,
    actions,
    gradient = DEFAULT_GRADIENT,
    haloColor = '#1faba7',
}: ErrorPageLayoutProps) {
    return (
        <Container
            maxWidth="md"
            sx={{
                minHeight: 'calc(100vh - 200px)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                py: 8,
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    animation: 'errorFadeIn 0.8s ease-out both',
                    '@keyframes errorFadeIn': {
                        from: { opacity: 0, transform: 'translateY(24px)' },
                        to: { opacity: 1, transform: 'translateY(0)' },
                    },
                }}
            >
                <Typography
                    component="h1"
                    aria-label={code}
                    sx={{
                        fontSize: { xs: '8rem', sm: '12rem', md: '16rem' },
                        fontWeight: 700,
                        lineHeight: 1,
                        letterSpacing: '-0.05em',
                        backgroundImage: gradient,
                        backgroundSize: '300% 100%',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        color: 'transparent',
                        WebkitTextFillColor: 'transparent',
                        animation:
                            'errorFloat 4s ease-in-out infinite, errorShimmer 6s linear infinite',
                        '@keyframes errorFloat': {
                            '0%, 100%': { transform: 'translateY(0)' },
                            '50%': { transform: 'translateY(-18px)' },
                        },
                        '@keyframes errorShimmer': {
                            '0%': { backgroundPosition: '0% 50%' },
                            '100%': { backgroundPosition: '300% 50%' },
                        },
                        '@media (prefers-reduced-motion: reduce)': {
                            animation: 'none',
                        },
                    }}
                >
                    {code}
                </Typography>
                <Box
                    aria-hidden
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: -1,
                        margin: 'auto',
                        width: { xs: 220, md: 360 },
                        height: { xs: 220, md: 360 },
                        borderRadius: '50%',
                        background: haloColor,
                        opacity: 0.12,
                        filter: 'blur(60px)',
                        animation: 'errorPulse 4s ease-in-out infinite',
                        '@keyframes errorPulse': {
                            '0%, 100%': { transform: 'scale(0.9)', opacity: 0.1 },
                            '50%': { transform: 'scale(1.1)', opacity: 0.2 },
                        },
                        '@media (prefers-reduced-motion: reduce)': {
                            animation: 'none',
                        },
                    }}
                />
            </Box>

            <Stack
                spacing={2}
                sx={{
                    alignItems: 'center',
                    mt: 2,
                    animation: 'errorFadeIn 0.8s ease-out 0.15s both',
                }}
            >
                <Typography
                    variant="h4"
                    component="p"
                    sx={{
                        fontWeight: 600,
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        color: 'text.secondary',
                        maxWidth: 460,
                    }}
                >
                    {description}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 2 }}>
                    {actions}
                </Stack>
            </Stack>
        </Container>
    );
}
